import * as actionTypes from '../../constants/actionTypes';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { credentialSchema } from '../../schemas';
import { updateFormData } from '../forms';
import { FORM_TYPE_CREATE_DATA_SOURCE } from '../../constants/formTypes';
import { has, isEmpty, pick } from 'lodash';
import { DELETE, GET, POST, PUT } from '../../constants/requestMethods';
import {
  ACLQueryModeTypes,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterViews,
} from '../../constants/visibilityFilters';
import {
  CONNECTION_CATEGORY_NEXLA_MONITOR,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
} from '../../constants/connectionCategories';
import Constants from '../../Constants';
import UIUtils from '../../lib/UIUtils';
import { getConnectionOrVendorType } from '../../lib/ResourceUtils';
import JSONUtils from '../../lib/JSONUtils';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { isCoreConnectorInOAuthMode } from 'new/utils/serverOAuthEligibility';

export const loadDataCredentialListing =
  (page, sortCriteria, availableFilters) => (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters },
      },
    } = getState();

    const aclQueryMode = accessControl.mode ?? ACLQueryModeTypes.MODE_OWNER.key;
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      filters[SearchFilterViews.CREDENTIALS],
      availableFilters,
    );
    let requestMethod;
    let endpoint;
    let payload;

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = '/data_credentials';
      payload = {
        page,
        per_page: Constants.LISTING_ITEMS_PER_PAGE,
        sort_by: sortKey,
        sort_order: sortOrder,
      };
    } else {
      requestMethod = POST;
      endpoint = `/data_credentials/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}`;
      payload = { filters: processedFilters };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_CREDENTIALS_REQUEST,
          actionTypes.GET_CREDENTIALS_SUCCESS,
          actionTypes.GET_CREDENTIALS_FAILURE,
        ],
        endpoint,
        schema: [credentialSchema],
        requestMethod,
        aclQueryMode,
        payload,
        requestInfo: { view: SearchFilterViews.CREDENTIALS },
      },
    }).then((response) => {
      return response;
    });
  };

export const loadDataCredentials =
  (
    //Always fetch creds  in owner mode
    aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key,
  ) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_ALL_CREDENTIALS_REQUEST,
          actionTypes.GET_ALL_CREDENTIALS_SUCCESS,
          actionTypes.GET_ALL_CREDENTIALS_FAILURE,
        ],
        endpoint: `/data_credentials`,
        schema: [credentialSchema],
        requestMethod: GET,
        aclQueryMode,
      },
    });
  };

export const loadDataCredentialsSearchSuggestions =
  (filters, availableFilters) => (dispatch, getState) => {
    const { app } = getState();
    const aclQueryMode = app.accessControl.mode;

    const { sortKey, sortOrder } =
      Constants.SortCriteriaInfo[Constants.SortCriteria.LAST_CREATED_DESC];
    const { processedFilters } = processSearchFilters(filters, availableFilters);

    if (isEmpty(processedFilters)) {
      return;
    }

    const params = new URLSearchParams({
      page: 1,
      per_page: RESOURCE_SUGGESTIONS_COUNT,
      sort_by: sortKey,
      sort_order: sortOrder,
    });

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.SEARCH_CREDENTIALS_REQUEST,
          actionTypes.SEARCH_CREDENTIALS_SUCCESS,
          actionTypes.SEARCH_CREDENTIALS_FAILURE,
        ],
        endpoint: `/data_credentials/search?${params}`,
        schema: [credentialSchema],
        requestMethod: POST,
        aclQueryMode,
        payload: { filters: processedFilters },
        requestInfo: { view: SearchFilterViews.CREDENTIALS },
      },
    });
  };

export const getDataCredential =
  (
    id,
    //Always fetch creds  in owner mode
    { aclQueryMode, ignoreErrors } = {
      aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
    },
  ) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_CREDENTIAL_REQUEST,
          actionTypes.GET_CREDENTIAL_SUCCESS,
          actionTypes.GET_CREDENTIAL_FAILURE,
        ],
        endpoint: `/data_credentials/${id}`,
        schema: credentialSchema,
        requestMethod: GET,
        aclQueryMode,
        ignoreErrors,
        payload: {
          expand: 1,
        },
      },
    });
  };

/**
 * Used when we want to fetch credential data without displaying loading or error messages.
 */
export const getDataCredentialSilent =
  (
    id,
    { aclQueryMode } = {
      aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
    },
  ) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_CREDENTIAL_SILENT_REQUEST,
          actionTypes.GET_CREDENTIAL_SILENT_SUCCESS,
          actionTypes.GET_CREDENTIAL_SILENT_FAILURE,
        ],
        endpoint: `/data_credentials/${id}`,
        schema: credentialSchema,
        requestMethod: GET,
        aclQueryMode,
        payload: {
          expand: 1,
        },
      },
    });
  };

export const addCredential = (type, info) => (dispatch, getState) => {
  if (!info) {
    dispatch({
      type: actionTypes.ADD_CREDENTIAL_FAILURE,
      payload: {
        error: 'Insufficient credential information',
      },
    });
    return Promise.reject({
      error: 'Insufficient credential information',
    });
  }

  let {
    cred_name,
    cred_desc,
    cred_type,
    id,
    auth_template_id,
    _vendorConnectionType,
    ...otherFields
  } = info;

  let payload = {
    name: cred_name,
    description: cred_desc,
    credentials_type: type,
  };

  // Attached below, inside the branch that actually writes `template_config`. An auth template
  // id on a core `credentials` payload describes nothing — gdrive in service-account mode and
  // sharepoint 2-legged are not created through a template — and only became reachable once the
  // opt-in header started returning `auth_templates` for the migrated core connectors. Flag off,
  // the old unconditional passthrough stands, so nothing moves for connectors whose category
  // keeps them out of that branch (email and script vendors among them).
  if (auth_template_id && !getFeatureFlagValue('UI_OAUTH_SERVER_FLOW')) {
    payload.auth_template_id = auth_template_id;
  }

  const {
    paging: { metaInfoById, connAuthById },
  } = getState();

  const meta = metaInfoById[type];

  // A credential created *through an auth template* takes the templatized shape, whatever the
  // connector's own category says. That is every templatized-API connector, plus a migrated core
  // connector in its three-legged mode — the migration gave those auth templates, so gdrive in
  // OAuth mode needs `template_config`, while gdrive in service-account mode still needs the
  // core `credentials` shape. Flag-gated: with the flag off this reduces to the category test.
  const isTemplatizedCategory =
    !!meta &&
    [CONNECTION_CATEGORY_TEMPLATIZED_API, CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI].includes(
      meta.connectionCategory,
    );
  const isCoreOAuthCredential =
    getFeatureFlagValue('UI_OAUTH_SERVER_FLOW') && isCoreConnectorInOAuthMode(type, info);

  if (meta && (isTemplatizedCategory || isCoreOAuthCredential)) {
    payload.vendor_id = meta.vendorId;

    if (!isEmpty(otherFields)) {
      delete otherFields.template;
      payload.template_config = otherFields;
      _typeCastCredentialConfigValues(payload, getState(), 'template_config');
    }

    payload.credentials_type = meta.connectionType;

    if (getFeatureFlagValue('UI_OAUTH_SERVER_FLOW')) {
      // Tells admin-api to resolve the hidden parameters it stripped from the form.
      payload.enrich_config = true;

      if (auth_template_id) {
        payload.auth_template_id = auth_template_id;
      }

      // A single-template connector renders no template selector, so nothing upstream sets
      // `auth_template_id` — the credential is still created *through* that template, and the
      // API falls back to it. Send it explicitly so the record is catalogued against the
      // template rather than inferred, and so a create matches what an edit already writes:
      // `CredentialForm` seeds `selectedTemplate` from `templates[0]` and sends it either way.
      // Only when there is exactly one; with several, the selector supplies the choice.
      if (payload.auth_template_id === undefined) {
        const templates = connAuthById?.[type]?.templates ?? [];
        if (templates.length === 1) {
          payload.auth_template_id = templates[0].id;
        }
      }
    }
  } else if (!isEmpty(otherFields)) {
    let credentials = { ...otherFields, credentials_type: type };

    if (has(otherFields, 'core_connector_type')) {
      //TODO: Temp - this should be removed after BE doesn't need credentials_type
      credentials.credentials_type = otherFields['core_connector_type'];
      // credentials.cred_type = otherFields['core_connector_type'];
      delete credentials['core_connector_type'];
    }

    _typeCastCredentialConfigValues(payload, getState());

    if (credentials.is_service_account === 'true' && credentials.json_creds) {
      credentials.json_creds = JSONUtils.parseJson(credentials.json_creds);

      if (credentials.json_creds) {
        credentials.project_id = credentials.json_creds.project_id;
      }

      delete credentials['gcs.client_id'];
      delete credentials['gcs.client_secret'];
    }

    // build nx_external.auth.props for min.io
    if (payload.credentials_type === 'min_io_s3') {
      credentials['nx_external.auth.props'] = JSON.stringify(
        pick(credentials, ['access_key_id', 'secret_key']),
      );

      delete credentials.access_key_id;
      delete credentials.secret_key;
    }

    // build sharepoint 2-legged token URL from tenant ID
    if (
      payload.credentials_type === 'sharepoint' &&
      credentials['oauth2.token_exchange_type'] === '2-legged'
    ) {
      credentials['oauth2.access.token.url'] =
        `https://login.microsoftonline.com/${credentials.tenant_id}/oauth2/v2.0/token`;
    }

    payload.credentials = credentials;
  }

  let endpoint;
  let requestMethod;

  if (id) {
    endpoint = `/data_credentials/${id}`;
    requestMethod = PUT;
  } else {
    endpoint = `/data_credentials?expand=1`;
    requestMethod = POST;
  }

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ADD_CREDENTIAL_REQUEST,
        actionTypes.ADD_CREDENTIAL_SUCCESS,
        actionTypes.ADD_CREDENTIAL_FAILURE,
      ],
      endpoint,
      schema: credentialSchema,
      requestMethod,
      payload,
    },
  });
};

export const verifyCredential =
  (
    id,
    //Always fetch  in owner mode
    aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key,
  ) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.PROBE_CREDENTIAL_REQUEST,
          actionTypes.PROBE_CREDENTIAL_SUCCESS,
          actionTypes.PROBE_CREDENTIAL_FAILURE,
        ],
        endpoint: `/data_credentials/${id}/probe`,
        schema: credentialSchema,
        requestMethod: GET,
        aclQueryMode,
        requestInfo: {
          id,
        },
      },
    });
  };

/**
 * Silently probes a credential (no global error UI) and refetches it.
 * Resolves an explicit envelope, or undefined when the probe is already in flight.
 */
export const verifyCredentialSilent =
  (
    id,
    aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key, // always fetch in owner mode
  ) =>
  (dispatch, getState) => {
    const state = getState();
    const isAlreadyProbing = state.credentials?.probingId === id;
    if (isAlreadyProbing) return Promise.resolve();

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.PROBE_CREDENTIAL_SILENT_REQUEST,
          actionTypes.PROBE_CREDENTIAL_SILENT_SUCCESS,
          actionTypes.PROBE_CREDENTIAL_SILENT_FAILURE,
        ],
        endpoint: `/data_credentials/${id}/probe`,
        schema: credentialSchema,
        requestMethod: GET,
        aclQueryMode,
        requestInfo: { id },
        ignoreErrors: true,
      },
    }).then(
      (response) => {
        // Refetch the credential so callers get credentials_non_secure_data. The
        // envelope is always truthy here, preserving the "probe executed" sentinel.
        const envelopeFor = (fetchResponse) => ({
          probeResponse: response,
          credential:
            fetchResponse?.entities?.credentials?.[id] ??
            // Refresh failed (e.g. 403 for collaborators); fall back to the store.
            getState().entities.credentials[id],
        });
        return dispatch(getDataCredentialSilent(id, { aclQueryMode })).then(envelopeFor, () =>
          envelopeFor(null),
        );
      },
      (error) => {
        return dispatch(getDataCredentialSilent(id, { aclQueryMode })).then(
          () => {
            throw error; // rethrow the original probe error
          },
          () => {
            throw error; // rethrow the original probe error
          },
        );
      },
    );
  };

export const deleteCredential = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_CREDENTIAL_REQUEST,
        actionTypes.DELETE_CREDENTIAL_SUCCESS,
        actionTypes.DELETE_CREDENTIAL_FAILURE,
      ],
      endpoint: `/data_credentials/${id}`,
      schema: credentialSchema,
      requestMethod: DELETE,
      requestInfo: {
        id,
      },
    },
  });
};

export const updateCredential =
  (id, info = {}) =>
  (dispatch, getState) => {
    const {
      paging: { metaInfoById },
      entities: { credentials, vendors },
    } = getState();

    const credential = credentials[id];

    if (!credential || !info) {
      return dispatch({
        type: actionTypes.UPDATE_CREDENTIAL_FAILURE,
        payload: {
          error: 'Insufficient credential information',
        },
      });
    }

    const { cred_name, cred_desc, auth_template_id, ...otherFields } = info;
    //check if credential is a vendor cred.
    const { credentials_type } = credential;

    const payload = {
      name: cred_name,
      description: cred_desc,
      credentials_type,
    };

    // Same rule as `addCredential`: the template id belongs to the templatized payload only.
    if (auth_template_id && !getFeatureFlagValue('UI_OAUTH_SERVER_FLOW')) {
      payload.auth_template_id = auth_template_id;
    }
    const vendorName = has(credential, 'vendor_id')
      ? getConnectionOrVendorType(credential, vendors, 'credentials_type', 'vendor_id')
      : null;

    const meta =
      metaInfoById && vendorName ? metaInfoById[vendorName] : metaInfoById[credentials_type];

    // Same decision as `addCredential` — an edit has to reach the same shape as the create, or
    // a PUT rewrites the credential into the wrong one. `enrich_config` matters more here than
    // on create: PUT rebuilds from the template, so hidden parameters the client never received
    // would otherwise be blanked.
    const isTemplatizedCategory =
      !!meta &&
      [CONNECTION_CATEGORY_TEMPLATIZED_API, CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI].includes(
        meta.connectionCategory,
      );
    const isCoreOAuthCredential =
      getFeatureFlagValue('UI_OAUTH_SERVER_FLOW') &&
      isCoreConnectorInOAuthMode(vendorName ?? credentials_type, info);

    if (meta && (isTemplatizedCategory || isCoreOAuthCredential)) {
      payload.vendor_id = meta.vendorId;

      if (!isEmpty(otherFields)) {
        delete otherFields.template;
        payload.template_config = otherFields;
        _typeCastCredentialConfigValues(payload, getState(), 'template_config');
      }

      payload.credentials_type = meta.connectionType;

      if (getFeatureFlagValue('UI_OAUTH_SERVER_FLOW')) {
        payload.enrich_config = true;

        if (auth_template_id) {
          payload.auth_template_id = auth_template_id;
        }
      }
    } else if (!isEmpty(otherFields)) {
      let credentials = { ...otherFields, credentials_type };

      if (has(otherFields, 'core_connector_type')) {
        //TODO: Temp - this should be removed after BE doesn't need credentials_type
        credentials.credentials_type = otherFields['core_connector_type'];
        // credentials.cred_type = otherFields['core_connector_type'];
        delete credentials['core_connector_type'];
      }

      _typeCastCredentialConfigValues(payload, getState());

      if (credentials.is_service_account === 'true' && credentials.json_creds) {
        credentials.json_creds = JSONUtils.parseJson(credentials.json_creds);

        if (credentials.json_creds) {
          credentials.project_id = credentials.json_creds.project_id;
        }

        delete credentials['gcs.client_id'];
        delete credentials['gcs.client_secret'];
      }

      // build nx_external.auth.props for min.io
      if (payload.credentials_type === 'min_io_s3') {
        credentials['nx_external.auth.props'] = JSON.stringify(
          pick(credentials, ['access_key_id', 'secret_key']),
        );

        delete credentials.access_key_id;
        delete credentials.secret_key;
      }

      // build sharepoint 2-legged token URL from tenant ID
      if (
        payload.credentials_type === 'sharepoint' &&
        credentials['oauth2.token_exchange_type'] === '2-legged'
      ) {
        credentials['oauth2.access.token.url'] =
          `https://login.microsoftonline.com/${credentials.tenant_id}/oauth2/v2.0/token`;
      }

      if (credentials_type !== CONNECTION_CATEGORY_NEXLA_MONITOR) {
        payload.credentials = credentials;
      }
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_CREDENTIAL_REQUEST,
          actionTypes.UPDATE_CREDENTIAL_SUCCESS,
          actionTypes.UPDATE_CREDENTIAL_FAILURE,
        ],
        endpoint: `/data_credentials/${id}?expand=1`,
        schema: credentialSchema,
        requestMethod: PUT,
        payload,
      },
    });
  };

export const addNewCredential = () => {
  return {
    type: actionTypes.ADD_NEW_CREDENTIAL,
  };
};

export const toggleExpandCredential = (id) => {
  return {
    type: actionTypes.TOGGLE_EXPAND_CREDENTIAL,
    id,
  };
};

export const editCredential = (credential, fields) => {
  return {
    type: actionTypes.EDIT_CREDENTIAL,
    credential,
    fields,
  };
};

export const cancelEditCredential = () => {
  return {
    type: actionTypes.CANCEL_EDIT_CREDENTIAL,
  };
};

export const modifyCredential = (key, value, fields) => {
  return {
    type: actionTypes.MODIFY_CREDENTIAL,
    key,
    value,
    fields,
  };
};

export const modifyAuthCredential = (key, value, fields) => {
  return {
    type: actionTypes.MODIFY_AUTH_CREDENTIAL,
    key,
    value,
    fields,
  };
};

export const probeCredentialAndFetch =
  (id, { silent = false } = {}) =>
  (dispatch) => {
    // Silent path: form flows that manage their own error UI. Caller handles rejection.
    if (silent) {
      return dispatch(verifyCredentialSilent(id));
    }
    // Default non-silent path: used by the "Test Credential" action on Credential Manager.
    // verifyCredential dispatches PROBE_CREDENTIAL_FAILURE → global ErrorDisplay on failure,
    // so errors are surfaced without the caller needing to .catch(). After the probe
    // resolves we refetch the credential so validStatus on the card refreshes.
    return dispatch(verifyCredential(id)).then(
      () => dispatch(getDataCredential(id)),
      (error) => {
        if (error?.code === 403) {
          return dispatch(getDataCredential(id));
        }
        // Swallow other errors — already surfaced via PROBE_CREDENTIAL_FAILURE.
      },
    );
  };

export const addCredentialAndProbe = (type, info) => (dispatch, getState) => {
  const {
    forms: { activeType },
  } = getState();

  return dispatch(addCredential(type, info)).then(
    (response) => {
      const savedId = response.result;
      if (activeType) {
        dispatch(updateFormData(activeType, { addedCredentialId: savedId }));
      }

      if (!!getState().forms[FORM_TYPE_CREATE_DATA_SOURCE]) {
        dispatch(
          updateFormData(FORM_TYPE_CREATE_DATA_SOURCE, {
            selectedCredential: getState().entities.credentials[savedId],
            selectedCredentialId: savedId,
          }),
        );
      }

      return dispatch(probeCredentialAndFetch(savedId, { silent: true }))
        .then((probeResponse) => {
          // Skip if probeResponse is falsy: verifyCredentialSilent returns
          // Promise.resolve() (undefined) when a concurrent probe is in flight.
          // Writing isValid:true without an actual result would be a false positive.
          if (probeResponse && !!getState().forms[FORM_TYPE_CREATE_DATA_SOURCE]) {
            dispatch(
              updateFormData(FORM_TYPE_CREATE_DATA_SOURCE, {
                credentialProbeStatus: { credential: savedId, isValid: true, message: null },
              }),
            );
          }
        })
        .catch((probeError) => {
          const message = probeError?.message || probeError?.error || 'Connection test failed.';
          if (!!getState().forms[FORM_TYPE_CREATE_DATA_SOURCE]) {
            dispatch(
              updateFormData(FORM_TYPE_CREATE_DATA_SOURCE, {
                credentialProbeStatus: { credential: savedId, isValid: false, message },
              }),
            );
          }
          return Promise.reject(probeError);
        });
    },
    (error) => {
      if (activeType) {
        dispatch(updateFormData(activeType, { saveError: error }));
      }
      dispatch({ type: actionTypes.ADD_CREDENTIAL_FAILURE, payload: {} });
      const saveErr = new Error(error?.message || 'Unable to save credential.');
      saveErr.isSaveError = true;
      return Promise.reject(saveErr);
    },
  );
};

export const updateCredentialAndProbe =
  (id, info, overrideActiveType = null) =>
  (dispatch, getState) => {
    const {
      forms: { activeType },
    } = getState();
    const _activeType = overrideActiveType || activeType;

    return dispatch(updateCredential(id, info)).then(
      (response) => {
        const savedId = response.result;
        if (_activeType) {
          dispatch(updateFormData(_activeType, { addedCredentialId: savedId }));
        }

        if (!!getState().forms[FORM_TYPE_CREATE_DATA_SOURCE]) {
          dispatch(
            updateFormData(FORM_TYPE_CREATE_DATA_SOURCE, {
              selectedCredential: getState().entities.credentials[savedId],
              selectedCredentialId: savedId,
            }),
          );
        }

        return dispatch(probeCredentialAndFetch(savedId, { silent: true }))
          .then((probeResponse) => {
            // Skip if probeResponse is falsy: verifyCredentialSilent returns
            // Promise.resolve() (undefined) when a concurrent probe is in flight.
            // Writing isValid:true without an actual result would be a false positive.
            if (probeResponse && !!getState().forms[FORM_TYPE_CREATE_DATA_SOURCE]) {
              dispatch(
                updateFormData(FORM_TYPE_CREATE_DATA_SOURCE, {
                  credentialProbeStatus: { credential: savedId, isValid: true, message: null },
                }),
              );
            }
          })
          .catch((probeError) => {
            const message = probeError?.message || probeError?.error || 'Connection test failed.';
            if (!!getState().forms[FORM_TYPE_CREATE_DATA_SOURCE]) {
              dispatch(
                updateFormData(FORM_TYPE_CREATE_DATA_SOURCE, {
                  credentialProbeStatus: { credential: savedId, isValid: false, message },
                }),
              );
            }
            return Promise.reject(probeError);
          });
      },
      (error) => {
        if (_activeType) {
          dispatch(updateFormData(_activeType, { saveError: error }));
        }
        dispatch({ type: actionTypes.UPDATE_CREDENTIAL_FAILURE, payload: { id } });
        const saveErr = new Error(error?.message || 'Unable to save credential.');
        saveErr.isSaveError = true;
        return Promise.reject(saveErr);
      },
    );
  };

const _typeCastCredentialConfigValues = (payload, state, propertyName = 'credentials') => {
  if (payload[propertyName] && payload.credentials_type) {
    const {
      paging: { connAuthById },
    } = state;
    const { templates, items } = connAuthById[payload.credentials_type] || {};
    payload[propertyName] = UIUtils.castToFieldType(payload[propertyName], items);
  }
};

export const getCredentialUsage = (credentialId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_CREDENTIAL_USAGE_REQUEST,
        actionTypes.GET_CREDENTIAL_USAGE_SUCCESS,
        actionTypes.GET_CREDENTIAL_USAGE_FAILURE,
      ],
      endpoint: `/data_credentials/${credentialId}/usage`,
      requestMethod: GET,
    },
  });
};
